import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormLabel,
  HStack,
  Input,
  Spinner,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import MainInput from "../../../../components/Form/input";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { openQuestionsListModel } from "../../../../modules/admin-modules/models/Actions";
import { getAllTagsRequest } from "../../../../modules/admin-modules/tags/Actions";
import { getMarkSchemesBySubjectRequest } from "../../../../modules/admin-modules/mark-schemes/Actions";
import {
  adminSelectStyles,
  withAdminSelectMenuPortal,
} from "../../../../components/Models/admin-modal-theme";
import { brandPalette } from "../../../../constants/brandPalette";

/** API datetime → value for input[type=datetime-local] */
function apiDateToDatetimeLocal(val) {
  if (val == null || val === "") return "";
  const s = String(val).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(:\d{2})?/);
  if (m) return `${m[1]}T${m[2]}`;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  return "";
}

const datetimeInputProps = {
  bg: "var(--surface-muted)",
  color: "var(--text-primary)",
  borderRadius: "12px",
  border: "1px solid",
  borderColor: "var(--border-subtle)",
};

function RadioErrorBox({ error, fieldName, children }) {
  return (
    <Box
      w="100%"
      data-exam-field={fieldName}
      p={error ? 3 : 0}
      borderRadius="md"
      borderWidth={error ? "1px" : "0"}
      borderColor={error ? "red.400" : "transparent"}
      bg={error ? "red.50" : "transparent"}
    >
      {children}
      {error ? (
        <Text color="red.500" fontSize="sm" fontWeight="600" mt={2}>
          {error}
        </Text>
      ) : null}
    </Box>
  );
}

const StepTwo = ({
  watchTotalQustions,
  watchSubType,
  watchQuestionWay,
  data,
  setValue,
  register,
  errors,
  isFormOne,
  watchChapters,
  watchExamTags,
  isAdd,
  watchType,
}) => {
  const dispatch = useDispatch();
  const [menuPortalTarget, setMenuPortalTarget] = useState(null);

  useEffect(() => {
    setMenuPortalTarget(document.body);
  }, []);

  const selectStyles = useMemo(
    () => withAdminSelectMenuPortal(adminSelectStyles()),
    []
  );
  const selectPortalProps = {
    menuPortalTarget:
      typeof document !== "undefined" ? document.body : menuPortalTarget,
    menuPosition: "fixed",
    maxMenuHeight: 240,
    menuShouldScrollIntoView: false,
  };

  const [fieldsCount, setFieldsCount] = useState(1);
  const [tagFieldsCount, setTagFieldsCount] = useState(1);
  const [selectedGroupOptions, setSelectedGroupOptions] = useState([]);
  const [groupDueDates, setGroupDueDates] = useState({});
  const [selectedMarkSchemeId, setSelectedMarkSchemeId] = useState(null);

  const chapters = useSelector((state) => state.chapters.chapters);
  const tags = useSelector((state) => state.adminTags.tags);
  const markSchemes = useSelector((state) => state.markSchemes.markSchemes);
  const chaptersLoading = useSelector((state) => state.chapters.isLoading);
  const groupsLoading = useSelector((state) => state.groups.isLoading);
  const tagsLoading = useSelector((state) => state.adminTags.isLoading);
  const markSchemesLoading = useSelector(
    (state) => state.markSchemes.isLoading
  );

  useEffect(() => {
    if (tags?.length === 0) dispatch(getAllTagsRequest());
  }, [dispatch, tags?.length]);

  useEffect(() => {
    if (!Array.isArray(markSchemes) || markSchemes.length === 0) {
      dispatch(getMarkSchemesBySubjectRequest());
    }
  }, [dispatch, markSchemes?.length]);

  useEffect(() => {
    if (watchSubType == 2 || watchSubType == 4) {
      setValue("chapters[1][no_of_questions]", 0);
    }
  }, [watchSubType, setValue]);

  useEffect(() => {
    register("groups", {
      validate: (value) =>
        (Array.isArray(value) && value.length > 0) ||
        "Select at least one group",
    });
  }, [register]);

  useEffect(() => {
    register("mark_scheme_id");
    register("group_due_dates");
  }, [register]);

  useEffect(() => {
    const chapterCount = data?.chapters?.length;
    setFieldsCount(!isAdd && chapterCount > 0 ? chapterCount : 1);

    const tagCount = data?.exam_tags?.length;
    setTagFieldsCount(!isAdd && tagCount > 0 ? tagCount : 1);

    const groupOpts = (data?.groups || []).map((val) => ({
      label: val?.name,
      value: val?.id,
    }));
    setSelectedGroupOptions(groupOpts);
    const dueMap = {};
    (data?.groups || []).forEach((group) => {
      const id = group?.id ?? group;
      if (id == null || id === "") return;
      dueMap[id] = apiDateToDatetimeLocal(group?.pivot?.due_date);
    });
    setGroupDueDates(dueMap);
    if (!isAdd && data?.id) {
      setValue(
        "groups",
        (data?.groups || []).map((g) => g?.id ?? g),
        { shouldValidate: false }
      );
      setValue("group_due_dates", dueMap, { shouldValidate: false });
    }
  }, [data?.id, isAdd, setValue]);

  useEffect(() => {
    const id =
      !isAdd && data?.id
        ? data?.mark_scheme_id ?? data?.mark_scheme?.id ?? null
        : null;
    setSelectedMarkSchemeId(id);
    if (id != null && id !== "") {
      setValue("mark_scheme_id", id, { shouldValidate: false });
    }
  }, [data?.id, data?.mark_scheme_id, data?.mark_scheme?.id, isAdd, setValue]);

  const groups = useSelector((state) => state.groups.groups);
  const performanceGroups = useSelector(
    (state) => state.groups.performanceGroups
  );

  const groupsOptions = groups?.map((data) => {
    return { value: data.id, label: data.name };
  });

  const performanceGroupsOptions =
    performanceGroups?.length > 0
      ? performanceGroups?.map((data) => {
        return { value: data.id, label: data.name };
      })
      : [];

  const activeChaptersOptions = useMemo(
    () =>
      (Array.isArray(chapters) ? chapters : [])
        .filter((c) => Number(c?.active) === 1)
        .map((c) => ({ value: c.id, label: c.name })),
    [chapters]
  );

  const questionContainerOptions = useMemo(
    () =>
      (Array.isArray(chapters) ? chapters : [])
        .filter((c) => Number(c?.active) === 0)
        .map((c) => ({ value: c.id, label: c.name })),
    [chapters]
  );

  const tagsOptions = tags?.map((data) => {
    return { value: data.id, label: data.name };
  });

  const markSchemeOptions = useMemo(
    () =>
      (Array.isArray(markSchemes) ? markSchemes : []).map((scheme) => ({
        value: scheme.id,
        label: scheme.title || scheme.name || `Scheme #${scheme.id}`,
      })),
    [markSchemes]
  );

  const selectedMarkSchemeOption = useMemo(() => {
    if (selectedMarkSchemeId == null || selectedMarkSchemeId === "") {
      return null;
    }
    const fromList = markSchemeOptions.find(
      (opt) => String(opt.value) === String(selectedMarkSchemeId)
    );
    if (fromList) return fromList;
    const nested = data?.mark_scheme;
    return {
      value: selectedMarkSchemeId,
      label:
        nested?.title || nested?.name || `Scheme #${selectedMarkSchemeId}`,
    };
  }, [selectedMarkSchemeId, markSchemeOptions, data?.mark_scheme]);

  const chapterQuestionCount = (ch) =>
    ch?.no_of_questions ?? ch?.pivot?.no_of_questions ?? 0;

  const chaptersForTotal = Array.isArray(watchChapters)
    ? watchChapters
    : watchChapters && typeof watchChapters === "object"
      ? Object.values(watchChapters)
      : [];

  const totalSelectedQuestions = chaptersForTotal.reduce((accumulator, value, idx) => {
    if (!value) return Number(accumulator);
    // Index 0 in array form / key "1" in object form is the primary chapter (always 0).
    const isPrimary =
      Array.isArray(watchChapters) ? idx === 0 : String(idx) === "1" || idx === 1;
    if (isPrimary) return Number(accumulator);
    return Number(accumulator) + Number(chapterQuestionCount(value));
  }, 0);

  const examTagsForTotal = Array.isArray(watchExamTags)
    ? watchExamTags
    : watchExamTags && typeof watchExamTags === "object"
      ? Object.values(watchExamTags)
      : [];

  const totalSelectedTagQuestions = examTagsForTotal.reduce(
    (accumulator, value) => {
      if (!value) return Number(accumulator);
      return (
        Number(accumulator) +
        Number(value?.question_number ?? value?.no_of_questions ?? 0)
      );
    },
    0
  );
  return (
    <VStack
      display={!isFormOne ? "flex" : "none"}
      dir="ltr"
      w="100%"
      flexWrap="wrap"
      justifyContent="space-between"
      spacing="50px"
      overflow="visible"
    >
      {(groupsLoading || chaptersLoading || tagsLoading || markSchemesLoading) && (
        <HStack w="100%" spacing={3} color="var(--text-muted)">
          <Spinner size="sm" color={brandPalette.primary} />
          <Text fontSize="sm" fontWeight="600">
            Loading groups, chapters, tags, and mark schemes...
          </Text>
        </HStack>
      )}
      <HStack w="100%" gap={5} mt={4} alignItems="flex-start">
        <Box w="45%" data-exam-field="groups">
          <FormLabel>Groups:</FormLabel>
          <Select
            options={groupsOptions}
            isMulti={true}
            isLoading={groupsLoading}
            onChange={(selected) => {
              const options = selected || [];
              setSelectedGroupOptions(options);
              setValue(
                "groups",
                options.map((val) => val.value),
                { shouldValidate: true }
              );
              setGroupDueDates((prev) => {
                const next = {};
                options.forEach((opt) => {
                  next[opt.value] = prev[opt.value] ?? "";
                });
                setValue("group_due_dates", next, { shouldDirty: true });
                return next;
              });
            }}
            styles={withAdminSelectMenuPortal(
              adminSelectStyles({ hasError: Boolean(errors?.groups) })
            )}
            placeholder="Select groups"
            value={selectedGroupOptions}
            {...selectPortalProps}
          />
          {errors?.groups?.message ? (
            <Text color="red.500" fontSize="sm" fontWeight="600" mt={2}>
              {errors.groups.message}
            </Text>
          ) : null}
          {selectedGroupOptions.length > 0 ? (
            <VStack align="stretch" spacing={3} mt={4}>
              <FormLabel mb={0}>Due time per group (optional)</FormLabel>
              {selectedGroupOptions.map((group) => (
                <Box key={group.value}>
                  <FormLabel fontSize="sm" mb={1}>
                    {group.label}
                  </FormLabel>
                  <Input
                    type="datetime-local"
                    value={groupDueDates[group.value] || ""}
                    onChange={(e) => {
                      const next = {
                        ...groupDueDates,
                        [group.value]: e.target.value,
                      };
                      setGroupDueDates(next);
                      setValue("group_due_dates", next, { shouldDirty: true });
                    }}
                    {...datetimeInputProps}
                  />
                </Box>
              ))}
            </VStack>
          ) : null}
        </Box>
        <Box w="45%" data-exam-field="mark_scheme_id">
          <FormLabel>Mark scheme:</FormLabel>
          <Select
            options={markSchemeOptions}
            isMulti={false}
            isClearable
            isLoading={markSchemesLoading}
            onChange={(selected) => {
              const id = selected?.value ?? "";
              setSelectedMarkSchemeId(id || null);
              setValue("mark_scheme_id", id, { shouldValidate: true });
            }}
            styles={withAdminSelectMenuPortal(
              adminSelectStyles({ hasError: Boolean(errors?.mark_scheme_id) })
            )}
            placeholder={
              markSchemeOptions.length
                ? "Select mark scheme"
                : "No mark schemes"
            }
            value={selectedMarkSchemeOption}
            {...selectPortalProps}
          />
          {errors?.mark_scheme_id?.message ? (
            <Text color="red.500" fontSize="sm" fontWeight="600" mt={2}>
              {errors.mark_scheme_id.message}
            </Text>
          ) : null}
        </Box>
      </HStack>
      <HStack w="100%" gap={5}>
        <Box w="45%">
          {performanceGroupsOptions?.map((data, index) => {
            return (
              <>
                <FormLabel>{data?.label}</FormLabel>
                <MainInput
                  label=""
                  defaultValues={data.question_number}
                  error={errors?.question_number?.message}
                  placeholder="Enter minimum target"
                  register={register}
                  name="question_number"
                  required={false}
                  onChange={(e) => {
                    setValue(
                      `chapters[${index + 1}][min_target]`,
                      e?.target?.value
                    );
                    setValue(
                      `chapters[${index + 1}][performance_gp_id]`,
                      data?.id
                    );
                  }}
                />
              </>
            );
          })}
        </Box>
      </HStack>
      <Box w="100%">
          <FormLabel>Question selection:</FormLabel>
          <RadioErrorBox fieldName="random" error={errors?.random?.message}>
          <Stack
            direction="row"
            role="group"
            aria-labelledby="my-radio-group"
            spacing="30px"
          >
            <label>
              <input
                {...register("random", { required: "This field is required" })}
                type="radio"
                value="0"
                defaultChecked={data?.random == 0 ? true : false}
              />
              Teacher selects questions
            </label>
            <label>
              <input
                {...register("random", { required: "This field is required" })}
                type="radio"
                value="1"
                defaultChecked={data?.random == 1 ? true : false}
              />
              Random questions from question bank
            </label>
          </Stack>
          </RadioErrorBox>
      </Box>
      <HStack w="100%" alignItems="flex-start">
        <VStack w="50%" alignItems="flex-start">
          <FormLabel>Scope:</FormLabel>
          <RadioErrorBox fieldName="sub_type" error={errors?.sub_type?.message}>
          <Stack
            direction="row"
            role="group"
            aria-labelledby="my-radio-group"
            spacing="30px"
          >
            <label>
              <input
                {...register("sub_type", {
                  required: "This field is required",
                })}
                type="radio"
                value="2"
                defaultChecked={data?.sub_type == 2 ? true : false}
              />
              Multiple chapters (comprehensive)
            </label>
            <label>
              <input
                {...register("sub_type", {
                  required: "This field is required",
                })}
                type="radio"
                value="4"
                disabled={watchQuestionWay == 0 ? true : false}
                defaultChecked={data?.sub_type == 4 ? true : false}
              />
              Exam from tags
            </label>
          </Stack>
          </RadioErrorBox>
        </VStack>
        <Box w="50%">
          <FormLabel w="30%">Number of questions:</FormLabel>
          <MainInput
            key={`question-number-${data?.id ?? "new"}-${data?.question_number ?? ""}`}
            label=""
            defaultValues={data?.question_number}
            error={errors?.question_number?.message}
            placeholder="Enter number of questions"
            register={register}
            name="question_number"
            required={false}
          />
        </Box>
        {!isAdd && watchQuestionWay == 0 && (
          <Button w="30%" onClick={() => dispatch(openQuestionsListModel())}>
            Choose questions
          </Button>
        )}
      </HStack>

      {watchSubType == 2 && watchTotalQustions && (
        <HStack w="100%" alignItems="flex-start">
          <VStack w="70%" alignItems="flex-start" spacing={4}>
            {[...Array(fieldsCount)].map((val, index) => {
              const chapter = data?.chapters?.[index];
              const isPrimary = index === 0;
              return (
                <Box w="100%" key={`chapter-row-${index}`}>
                  <FormLabel>
                    {isPrimary ? "Chapter:" : "Question container:"}
                  </FormLabel>
                  <HStack w="100%" spacing="40px" alignItems="flex-end">
                    <Box w={isPrimary ? "100%" : "45%"}>
                      <Select
                        options={
                          isPrimary
                            ? activeChaptersOptions
                            : questionContainerOptions
                        }
                        isMulti={false}
                        isLoading={chaptersLoading}
                        onChange={(selected) => {
                          setValue(
                            `chapters[${index + 1}][chapter_id]`,
                            selected?.value
                          );
                          if (isPrimary) {
                            setValue(`chapters[${index + 1}][no_of_questions]`, 0);
                          }
                        }}
                        styles={selectStyles}
                        placeholder={
                          isPrimary
                            ? "Select chapter"
                            : "Select question container"
                        }
                        menuPlacement="top"
                        defaultValue={
                          chapter
                            ? { label: chapter?.name, value: chapter?.id }
                            : undefined
                        }
                        {...selectPortalProps}
                      />
                    </Box>

                    {!isPrimary && (
                      <HStack w="45%">
                        <FormLabel w="60%">Questions count:</FormLabel>
                        <Input
                          size="md"
                          defaultValue={
                            chapter ? chapterQuestionCount(chapter) : ""
                          }
                          onChange={(e) => {
                            setValue(
                              `chapters[${index + 1}][no_of_questions]`,
                              e.target.value
                            );
                          }}
                          placeholder="Enter questions count"
                          name={`chapters_${index + 1}_no_of_questions`}
                        />
                      </HStack>
                    )}
                  </HStack>
                </Box>
              );
            })}
          </VStack>
          <Button
            w="30%"
            onClick={() => setFieldsCount((s) => s + 1)}
            isDisabled={
              totalSelectedQuestions >= watchTotalQustions ? true : false
            }
          >
            Add another question container
          </Button>
        </HStack>
      )}
      {watchSubType == 4 && watchTotalQustions && (
        <VStack w="100%" alignItems="flex-start" spacing="30px">
          <Box w="70%">
            <FormLabel>Chapter:</FormLabel>
            <Select
              options={activeChaptersOptions}
              isMulti={false}
              isLoading={chaptersLoading}
              onChange={(data) => {
                setValue("chapters[1][chapter_id]", data?.value);
                setValue("chapters[1][no_of_questions]", 0);
              }}
              styles={selectStyles}
              placeholder="Select chapter"
              menuPlacement="top"
              defaultValue={
                data?.chapters?.[0]
                  ? {
                      label:
                        data.chapters[0]?.name ??
                        data.chapters[0]?.chapter?.name,
                      value:
                        data.chapters[0]?.id ?? data.chapters[0]?.chapter_id,
                    }
                  : undefined
              }
              {...selectPortalProps}
            />
          </Box>
          <HStack w="100%" alignItems="flex-start">
            <VStack w="70%" alignItems="flex-start">
              <FormLabel>Tags:</FormLabel>

            {[...Array(tagFieldsCount)].map((val, index) => {
              const examTag =
                data?.exam_tags?.[index] ?? data?.exam_tags?.[index + 1];
              return (
                <HStack w="100%" spacing="40px" key={index}>
                  <Box w="45%">
                    <Select
                      options={tagsOptions}
                      isMulti={false}
                      isLoading={tagsLoading}
                      onChange={(data) => {
                        setValue(
                          `exam_tags[${index + 1}][question_tag_id]`,
                          data?.value
                        );
                      }}
                      styles={selectStyles}
                      placeholder="Select tag"
                      menuPlacement="top"
                      defaultValue={
                        examTag
                          ? {
                              label: examTag?.name ?? examTag?.tag?.name,
                              value:
                                examTag?.question_tag_id ??
                                examTag?.tag_id ??
                                examTag?.id,
                            }
                          : undefined
                      }
                      {...selectPortalProps}
                    />
                  </Box>

                  <HStack w="45%">
                    <FormLabel w="60%">Questions count:</FormLabel>
                    <Input
                      size="md"
                      defaultValue={
                        examTag?.question_number ?? examTag?.no_of_questions
                      }
                      onChange={(e) => {
                        setValue(
                          `exam_tags[${index + 1}][question_number]`,
                          e.target.value
                        );
                      }}
                      placeholder="Enter questions count"
                      name="tag_question_number"
                    />
                  </HStack>
                </HStack>
              );
            })}
          </VStack>
          <Button
            w="30%"
            onClick={() => setTagFieldsCount((s) => s + 1)}
            isDisabled={
              totalSelectedTagQuestions >= watchTotalQustions ? true : false
            }
          >
            Add another tag
          </Button>
        </HStack>
        </VStack>
      )}
    </VStack>
  );
};

export default StepTwo;
